import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { LoginMethod } from "@/graphql/generated/graphql";

import { useCompleteLogin } from "../hooks/use-complete-login";
import { discoverLoginMethod, login, startFederatedLogin } from "../services/auth.service";
import { rememberFederatedReturnPath } from "../services/federated-return-path";
import { isSafeInternalPath } from "../services/is-safe-internal-path";

import { TwoFactorVerificationForm } from "./2fa-verification-form";
import { resolveLoginErrorKey } from "./resolve-login-error-key";
import { resolveSsoErrorKey } from "./resolve-sso-error-key";

type LoginStep = "email" | "password";

function getSafeRedirectPath(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return isSafeInternalPath(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

interface LoginFormProps {
  redirect?: string | null | undefined;
}

type LoginFormMessageKey =
  | ReturnType<typeof resolveLoginErrorKey>
  | ReturnType<typeof resolveSsoErrorKey>
  | "login.passwordRequired";

export default function LoginForm({ redirect }: LoginFormProps) {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messageKey, setMessageKey] = useState<LoginFormMessageKey | null>(() => {
    const ssoFailure = searchParams.get("ssoError");
    return ssoFailure ? resolveSsoErrorKey(ssoFailure) : null;
  });
  const { t } = useTranslation("auth");
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const resolveReturnPath = () => (redirect ? getSafeRedirectPath(redirect) : null);
  const { twoFactorToken, applyOutcome, completeWithReturnPath, forgetTwoFactorToken } = useCompleteLogin({
    resolveReturnPath,
  });

  useEffect(() => {
    const input = step === "password" ? passwordInputRef.current : emailInputRef.current;
    input?.focus();
  }, [step]);

  const handOffToFederatedLogin = (startUrl: string): void => {
    rememberFederatedReturnPath(resolveReturnPath());
    startFederatedLogin(startUrl);
  };

  const runDiscovery = async (): Promise<void> => {
    const discovery = await discoverLoginMethod(email);
    if (discovery.method === LoginMethod.Federated && discovery.startUrl) {
      handOffToFederatedLogin(discovery.startUrl);
      return;
    }
    setStep("password");
  };

  const runLogin = async (): Promise<void> => {
    if (!password) {
      setMessageKey("login.passwordRequired");
      return;
    }

    const response = await login(email, password);
    if ("requiresFederatedLogin" in response) {
      handOffToFederatedLogin(response.startUrl);
      return;
    }

    applyOutcome(response);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setMessageKey(null);

    try {
      await (step === "email" ? runDiscovery() : runLogin());
    } catch (error) {
      setMessageKey(resolveLoginErrorKey(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setStep("email");
    setPassword("");
    setMessageKey(null);
  };

  const handle2FABack = () => {
    forgetTwoFactorToken();
    setMessageKey(null);
  };

  if (twoFactorToken) {
    return (
      <TwoFactorVerificationForm
        twoFactorToken={twoFactorToken}
        onSuccess={completeWithReturnPath}
        onBack={handle2FABack}
      />
    );
  }

  const isPasswordStep = step === "password";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-4 bg-background px-4 py-8 sm:px-16">
      <div>
        <label htmlFor="email" className="block text-xs text-foreground">
          {t("login.emailLabel")}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          ref={emailInputRef}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t("login.emailPlaceholder")}
          autoComplete="username"
          readOnly={isPasswordStep}
          required
          className="mt-1 block w-full appearance-none rounded-md border bg-transparent px-3 py-2 shadow-xs placeholder:text-muted-foreground read-only:text-muted-foreground focus:border-foreground focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus sm:text-sm"
        />
      </div>
      <div hidden={!isPasswordStep}>
        <label htmlFor="password" className="block text-xs text-foreground">
          {t("login.passwordLabel")}
        </label>
        <input
          id="password"
          name="password"
          type="password"
          ref={passwordInputRef}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          className="mt-1 block w-full appearance-none rounded-md border bg-transparent px-3 py-2 shadow-xs placeholder:text-muted-foreground focus:border-foreground focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus sm:text-sm"
        />
      </div>
      <div className="text-sm text-destructive">{messageKey && <p>{t(messageKey)}</p>}</div>
      <Button
        type="submit"
        disabled={isLoading}
        className="w-full"
        data-testid={isPasswordStep ? "login-button" : "continue-button"}
      >
        {isLoading ? <LoadingDots color="#808080" /> : <p>{t(isPasswordStep ? "login.submit" : "login.continue")}</p>}
      </Button>
      {isPasswordStep && (
        <Button
          type="button"
          variant="link"
          onClick={handleUseDifferentEmail}
          disabled={isLoading}
          data-testid="use-different-email"
        >
          {t("login.useDifferentEmail")}
        </Button>
      )}
    </form>
  );
}
